// Pictures of companions for the menu, drawn by the engine. A companion's still picture is drawn
// once at one art pixel and cropped (sprite), then shown at a whole number of pixels per art pixel
// (picture), so it stays crisp at any size. Pictures are drawn when they come into view, so a list
// of 85 companions or a dog's 23 skins doesn't hold up opening the menu.
import { env } from '../engine/core.js';
import { clockNow } from '../engine/clock.js';
import { makeCompanion, drawCompanion } from '../engine/companion.js';
import { settingsOf } from '../engine/settings.js';

// A companion drawn still into a canvas, as big as fits, standing on its bottom edge. room: spare
// columns beside it. (The website's page API draws its pictures with this.)
export function drawPortrait(canvas, me, { room = 6 } = {}) {
  const d = me.design, art = d.body.art;
  const W = (art?.[0].length ?? 12) + room, H = (art?.length ?? 5) + (d.body.legs?.length ?? 1) + 6;
  const s = Math.max(1, Math.floor(Math.min(canvas.width / W, canvas.height / H)));
  const c = makeCompanion(d, s);
  Object.assign(c, { look: me.look, outfit: { ...me.outfit }, x: canvas.width / 2, y: canvas.height - 2 * s });
  const ctx = canvas.getContext('2d'), keep = env.ctx;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = false;
  env.ctx = ctx;
  try { drawCompanion(c, clockNow()); } finally { env.ctx = keep; }
}

// A design in a look and outfit, drawn still at one art pixel and cropped to what was drawn:
// { canvas, w, h }. With a parachute ({ color, pattern }) it hangs under it, open. Kept, so the
// same picture is drawn once (a few hundred at most).
const sprites = new Map();
const KEEP = 600;
export function sprite(design, look, outfit = { head: 'none', body: 'none' }, parachute = null) {
  const key = `${design.id}|${look.key}|${outfit.head}|${outfit.body}|${parachute ? parachute.color + ':' + parachute.pattern : ''}`;
  if (sprites.has(key)) return sprites.get(key);
  const art = design.body.art;
  const W = (art?.[0].length ?? 12) + 60, H = (art?.length ?? 5) + (design.body.legs?.length ?? 1) + 60;   // room for hats, wings and tails
  const work = document.createElement('canvas');
  work.width = W; work.height = H;
  const ctx = work.getContext('2d'), keep = env.ctx;
  const c = makeCompanion(design, 1);
  Object.assign(c, { look, outfit: { ...outfit }, x: Math.round(W / 2), y: H - 20 });
  if (parachute) Object.assign(c, { chute: 1, parachute: { ...parachute } });
  env.ctx = ctx;
  try { drawCompanion(c, clockNow()); } finally { env.ctx = keep; }
  const px = ctx.getImageData(0, 0, W, H).data;
  let x0 = W, y0 = H, x1 = -1, y1 = -1;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (px[(y * W + x) * 4 + 3] === 0) continue;
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const out = document.createElement('canvas');
  out.width = Math.max(1, x1 - x0 + 1); out.height = Math.max(1, y1 - y0 + 1);
  if (x1 >= 0) out.getContext('2d').drawImage(work, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  const made = { canvas: out, w: out.width, h: out.height };
  sprites.set(key, made);
  if (sprites.size > KEEP) sprites.delete(sprites.keys().next().value);
  return made;
}

// Just the canopy of a sprite drawn with its parachute: its top rows (props.js: six rows high).
const CANOPY_ROWS = 6;
export function canopy(sp) {
  const out = document.createElement('canvas');
  out.width = sp.w; out.height = Math.min(CANOPY_ROWS, sp.h);
  out.getContext('2d').drawImage(sp.canvas, 0, 0, sp.w, out.height, 0, 0, sp.w, out.height);
  return { canvas: out, w: out.width, h: out.height };
}

// The whole number of pixels per art pixel at which every one of these fits a box (at least 1,
// at most max).
export function fitScale(list, w, h, max = 6) {
  const k = Math.min(...list.map(s => Math.min(w / s.w, h / s.h)));
  return Math.max(1, Math.min(max, Math.floor(k)));
}

// A sprite shown at k pixels per art pixel, in a canvas of its own.
export function picture(sp, k, cls = 'spr') {
  const canvas = document.createElement('canvas');
  canvas.width = sp.w * k; canvas.height = sp.h * k;
  canvas.className = cls;
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(sp.canvas, 0, 0, canvas.width, canvas.height);
  return canvas;
}

// A picture that draws itself once it comes into view: an empty canvas of the right size until
// then. `make()` returns the sprite; k its scale.
let watcher = null;
const pending = new WeakMap();
export function lazyPicture(make, size, cls = 'spr') {
  const canvas = document.createElement('canvas');
  canvas.width = size.w; canvas.height = size.h;
  canvas.className = cls;
  canvas.setAttribute('aria-hidden', 'true');
  if (typeof IntersectionObserver !== 'function') { paintInto(canvas, make()); return canvas; }
  watcher ??= new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const draw = pending.get(entry.target);
      watcher.unobserve(entry.target);
      pending.delete(entry.target);
      draw?.();
    }
  }, { rootMargin: '200px' });
  pending.set(canvas, () => paintInto(canvas, make()));
  watcher.observe(canvas);
  return canvas;
}
// A companion's small picture in its looks, drawn when it comes into view. The list of companions
// shows them in LIST_BOX (by its spacing), and so does a Show me's partner picker, so the two show
// everyone alike.
export const LIST_BOX = { comfortable: { w: 36, h: 32 }, compact: { w: 30, h: 26 } };
export function smallPicture(me, size) {
  const outfit = settingsOf(me).outfit;
  return lazyPicture(() => sprite(me.design, me.look, outfit), size);
}

// A sprite into a canvas made for it, as big as fits at a whole scale, standing on its bottom edge.
function paintInto(canvas, sp) {
  const k = fitScale([sp], canvas.width, canvas.height, 3);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const w = sp.w * k, h = sp.h * k;
  ctx.drawImage(sp.canvas, Math.round((canvas.width - w) / 2), canvas.height - h, w, h);
}

// A sprite's own main color: the most common clearly colored pixel, outlines, whites and greys
// left out (null for a companion that is all black, white or grey). For the touch of its color
// on its page.
const colors = new WeakMap();
export function mainColor(sp) {
  if (colors.has(sp)) return colors.get(sp);
  const px = sp.canvas.getContext('2d').getImageData(0, 0, sp.w, sp.h).data;
  const count = new Map(), hues = new Map();
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] < 200) continue;
    const [hue, light, sat] = hsl(px[i], px[i + 1], px[i + 2]);
    if (light < 0.18 || light > 0.9 || sat < 0.3) continue;
    const rgb = (px[i] << 16) | (px[i + 1] << 8) | px[i + 2], bucket = Math.round(hue * 24) % 24;
    count.set(rgb, (count.get(rgb) || 0) + 1);
    hues.set(bucket, (hues.get(bucket) || 0) + 1);
  }
  let color = null;
  if (hues.size) {
    const top = [...hues].sort((a, b) => b[1] - a[1])[0][0];
    let best = -1;
    for (const [rgb, n] of count) {
      const hue = hsl(rgb >> 16, (rgb >> 8) & 255, rgb & 255)[0];
      if (Math.round(hue * 24) % 24 === top && n > best) { best = n; color = '#' + rgb.toString(16).padStart(6, '0'); }
    }
  }
  colors.set(sp, color);
  return color;
}
function hsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), light = (max + min) / 2, d = max - min;
  if (!d) return [0, light, 0];
  const sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
  const hue = max === r ? ((g - b) / d + (g < b ? 6 : 0)) / 6 : max === g ? ((b - r) / d + 2) / 6 : ((r - g) / d + 4) / 6;
  return [hue, light, sat];
}
